/**
 * IncomeDetailsModal.jsx
 * DAS-2621: Drill-down modal for the "Return by part" matrix. Clicking an "Inc €" cell
 * (part × period) opens this MUI modal listing the underlying dividend/income payout
 * transactions that sum to the displayed value.
 *
 * Data source: GET /api/portfolio_income_details/<userId>?part=<part>&period=<period>
 * (backend reuses the exact same sources as the aggregated cell, so rows reconcile).
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from '../functions/api';
import getUserId from '../functions/getUserId';

const formatEuro = (value) => {
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) return '€ 0,00';
  return `€ ${num.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const IncomeDetailsModal = ({ open, onClose, part, partLabel, period, periodLabel }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !part || !period) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Shared axios instance already prefixes /api → do NOT add it here (DAS-2550).
    api
      .get(`/portfolio_income_details/${getUserId()}`, { params: { part, period } })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data?.transactions || []);
        setTotal(res.data?.total_eur ?? 0);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error loading income details:', err);
        setError('Could not load payout details.');
        setRows([]);
        setTotal(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, part, period]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }} data-testid="income-payouts-modal-title">
        Income payouts — {partLabel} · {periodLabel}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (t) => t.palette.grey[500] }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ py: 2 }}>
            {error}
          </Typography>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No payouts in this period.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Holding</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{formatDate(r.value_date)}</TableCell>
                  <TableCell>{r.symbol || '—'}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{r.type || 'dividend'}</TableCell>
                  <TableCell align="right" sx={{ color: (parseFloat(r.amount_eur) < 0 ? '#c62828' : '#2e7d32'), fontWeight: 600 }}>
                    {formatEuro(r.amount_eur)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={3} sx={{ fontWeight: 700, borderBottom: 'none' }}>
                  Total ({rows.length} {rows.length === 1 ? 'payout' : 'payouts'})
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, borderBottom: 'none' }}>
                  {formatEuro(total)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IncomeDetailsModal;
